import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { addToFavorites, removeFromFavorites, isInFavorites } from '../utils/favoritesService';

const HeartButton = styled.button`
  position: absolute;
  top: 10px;
  left: 10px;
  width: 36px;
  height: 36px;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 5;
  
  &:hover {
    background: rgba(0, 0, 0, 0.7);
    transform: scale(1.1);
  }
  
  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
  }
`;

const HeartIcon = styled.svg`
  width: 18px;
  height: 18px;
  stroke: white;
  stroke-width: 2;
  fill: ${props => props.$isFavorite ? '#ff5a5a' : 'none'};
  transition: fill 0.2s ease;

  @media (max-width: 768px) {
    width: 16px;
    height: 16px;
  }
`;

const FavoriteTooltip = styled.div`
  position: absolute;
  top: 50px;
  left: 0;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 0.8rem;
  white-space: nowrap;
  pointer-events: none;
  opacity: ${props => props.$visible ? 1 : 0};
  transform: translateY(${props => props.$visible ? '0' : '-10px'});
  transition: opacity 0.2s ease, transform 0.2s ease;
  z-index: 10;
`;

function FavoriteButton({ item }) {
  const [isFavorite, setIsFavorite] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipText, setTooltipText] = useState('');
  
  // Check if the item is already in favorites
  useEffect(() => {
    if (item && item.id) {
      setIsFavorite(isInFavorites(item.id));
    }
  }, [item]);
  
  const toggleFavorite = () => {
    if (!item) return;
    
    let success;
    
    if (isFavorite) {
      success = removeFromFavorites(item.id);
      if (success) {
        setIsFavorite(false);
        showTooltip('Removed from favorites');
      }
    } else {
      // Format the item to include all necessary information
      // Items can have either 'image' or 'imagePath' property
      const imagePathValue = item.image || item.imagePath;
      const fullImagePath = imagePathValue?.includes('/')
        ? imagePathValue
        : `/images/collections/${item.collectionFolder}/${imagePathValue}`;

      const favoriteItem = {
        id: item.id,
        title: item.title,
        imagePath: fullImagePath,
        medium: item.medium,
        size: item.size,
        year: item.year,
        price: item.price,
        available: item.available,
        status: item.status,
        collectionId: item.collectionId,
        collectionTitle: item.collectionTitle
      };
      
      success = addToFavorites(favoriteItem);
      if (success) {
        setIsFavorite(true);
        showTooltip('Added to favorites');
      }
    }
  };
  
  const showTooltip = (text) => {
    setTooltipText(text);
    setTooltipVisible(true);
    
    // Hide tooltip after 2 seconds
    setTimeout(() => {
      setTooltipVisible(false);
    }, 2000);
  };
  
  if (!item) return null;
  
  return (
    <>
      <HeartButton
        onClick={(e) => {
          e.stopPropagation();
          toggleFavorite();
        }}
        aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
      >
        <HeartIcon
          $isFavorite={isFavorite}
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
        </HeartIcon>
        <FavoriteTooltip $visible={tooltipVisible}>
          {tooltipText}
        </FavoriteTooltip>
      </HeartButton>
    </>
  );
}

export default FavoriteButton;